import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Mail, RefreshCw, Trash2 } from 'lucide-react'
import toast from 'react-hot-toast'
import { apiErrorDetail } from '@/lib/api/client'
import {
  INVITATIONS_QUERY_KEY,
  invitationApi,
  type Invitation,
  type InvitationIssued,
} from '@/lib/api/invitations'
import { useAuth } from '@/contexts/AuthContext'
import { UserRole } from '@/types/user'

interface PendingInvitationsProps {
  /** Called with the fresh link after a resend so the parent can show it. */
  onResent: (issued: InvitationIssued) => void
}

const formatRole = (role: string) =>
  role.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')

const ADMIN_ONLY_HINT = 'Only super admins can manage admin invitations'

/**
 * Table of invitations that have not yet been accepted.
 *
 * Each row offers "Resend" (issues a fresh link and hands it to the parent via
 * `onResent`) and "Revoke" (after confirmation). Admins may only manage viewer
 * invitations; rows they cannot act on have their buttons disabled so the UI
 * never issues a request the backend would reject with 403. Renders nothing
 * when there are no pending invitations, and an inline alert if the list
 * cannot be loaded.
 */
export function PendingInvitations({ onResent }: PendingInvitationsProps) {
  const queryClient = useQueryClient()
  const { user: currentUser } = useAuth()
  const isSuperAdmin = currentUser?.role === UserRole.SUPER_ADMIN
  const { data: invitations = [], isError, error } = useQuery({
    queryKey: INVITATIONS_QUERY_KEY,
    queryFn: () => invitationApi.list('pending'),
  })

  const resend = useMutation({
    mutationFn: (id: string) => invitationApi.resend(id),
    onSuccess: (issued) => {
      queryClient.invalidateQueries({ queryKey: INVITATIONS_QUERY_KEY })
      onResent(issued)
    },
    onError: (err: unknown) => toast.error(apiErrorDetail(err) || 'Failed to resend invitation'),
  })
  const revoke = useMutation({
    mutationFn: (id: string) => invitationApi.revoke(id),
    onSuccess: () => {
      toast.success('Invitation revoked')
      queryClient.invalidateQueries({ queryKey: INVITATIONS_QUERY_KEY })
    },
    onError: (err: unknown) => toast.error(apiErrorDetail(err) || 'Failed to revoke invitation'),
  })

  if (isError) {
    const detail = apiErrorDetail(error)
    return (
      <p role="alert" className="text-sm text-red-700 dark:text-red-300">
        Couldn&apos;t load pending invitations.{detail ? ` ${detail}` : ''}
      </p>
    )
  }

  if (invitations.length === 0) return null

  return (
    <div className="bg-white dark:bg-[#000742]/50 rounded-xl border border-gray-200 dark:border-[#5869ff]/20 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-[#5869ff]/20 flex items-center gap-2">
        <Mail className="w-5 h-5 text-[#5869ff]" />
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Pending invitations</h2>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-[#1a1f5c]/30">
            <tr className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              <th scope="col" className="px-6 py-3">Email</th>
              <th scope="col" className="px-6 py-3">Role</th>
              <th scope="col" className="px-6 py-3">Invited by</th>
              <th scope="col" className="px-6 py-3">Expires</th>
              <th scope="col" className="px-6 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-[#5869ff]/20">
            {invitations.map((invitation: Invitation) => {
              const canManage = isSuperAdmin || invitation.role === UserRole.VIEWER
              const resending = resend.isPending && resend.variables === invitation.id
              const revoking = revoke.isPending && revoke.variables === invitation.id
              const hint = canManage ? undefined : ADMIN_ONLY_HINT
              return (
                <tr key={invitation.id} className="text-sm text-gray-900 dark:text-white">
                  <td className="px-6 py-3">{invitation.email}</td>
                  <td className="px-6 py-3">{formatRole(invitation.role)}</td>
                  <td className="px-6 py-3 text-gray-600 dark:text-gray-400">{invitation.invited_by_name ?? '—'}</td>
                  <td className="px-6 py-3 text-gray-600 dark:text-gray-400">
                    {new Date(invitation.expires_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => resend.mutate(invitation.id)}
                        disabled={!canManage || resending}
                        title={hint}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-300 dark:border-[#5869ff]/30 hover:bg-gray-100 dark:hover:bg-[#1a1f5c] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <RefreshCw className={`w-4 h-4 ${resending ? 'animate-spin' : ''}`} /> Resend
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (confirm(`Revoke the invitation for ${invitation.email}?`)) revoke.mutate(invitation.id)
                        }}
                        disabled={!canManage || revoking}
                        title={hint}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-red-300 dark:border-red-500/40 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <Trash2 className="w-4 h-4" /> Revoke
                      </button>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
